import React from "react";
import Section from "./Section";
import SectionHeading from "./SectionHeading";
import TrendLineChart from "../analytics/TrendLineChart";
import { chartColors } from "../analytics/chartTheme";
import { analyticsTrend } from "./heroFixtures";

/**
 * The real TrendLineChart component (frontend/src/components/analytics/),
 * fed fixture data shaped exactly like /api/analytics's trend array — same
 * reasoning as ComplexitySpotlight reusing the real PrCard: it can't drift
 * from what /analytics actually renders, since it IS what /analytics
 * renders. Backed by backlog item 14, live in production since 2026-08-15.
 *
 * Cold-start note (checked 2026-08-27, no copy change needed): unlike file
 * expertise, there is NO historical backfill for analytics — team_analytics_daily
 * only accumulates from a team's own start date (see meta.first_recorded_date),
 * so a new team's charts begin empty and fill forward. The copy below is
 * deliberately phrased in present/ongoing terms ("one day at a time", "right
 * now"). Don't rewrite it to promise historical trends on day one.
 */
const ANNOTATIONS = [
  {
    label: "Opened vs. resolved",
    body: "The single chart that shows whether your team is keeping up with incoming PRs — or falling behind, one day at a time.",
  },
  {
    label: "Complexity trend",
    body: "Is the code landing in review getting harder to review, not just more frequent?",
  },
  {
    label: "Reviewer workload",
    body: "See who's carrying the queue right now, before it becomes a bottleneck for the whole team.",
  },
  {
    label: "Aging PRs",
    body: "Nothing sits in review purely because it fell out of sight — surfaced automatically, not chased down manually.",
  },
];

export default function AnalyticsSpotlight() {
  return (
    <Section id="analytics">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div className="mx-auto w-full max-w-lg">
          <div
            aria-hidden="true"
            className="rounded-2xl border border-rf-border bg-rf-bg-secondary p-5 shadow-2xl"
          >
            <div className="mb-4 text-sm font-semibold text-rf-text-primary">
              PR Volume vs. Resolution
            </div>
            <TrendLineChart
              data={analyticsTrend}
              ariaLabel="Daily count of PRs opened versus PRs resolved"
              series={[
                { dataKey: "prs_opened", label: "Opened", color: chartColors.blue },
                { dataKey: "prs_resolved", label: "Resolved", color: chartColors.accent, dashed: true },
              ]}
              height={220}
            />
          </div>
        </div>

        <div>
          <SectionHeading
            eyebrow="Team analytics"
            title="See the bottleneck before it's a backlog"
            align="left"
          />
          <dl className="mt-8 flex flex-col gap-6">
            {ANNOTATIONS.map((item) => (
              <div key={item.label}>
                <dt className="mb-1 text-sm font-semibold text-rf-text-primary">
                  {item.label}
                </dt>
                <dd className="text-sm leading-relaxed text-rf-text-secondary">{item.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </Section>
  );
}
